import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ShieldCheck, Zap } from "lucide-react";
import { MatchRing, BrokerMark } from "@/components/shared/Primitives";
import { useBrokers } from "@/context/BrokersContext";

const ease = [0.2, 0.8, 0.2, 1];

const CRITERIA = [
  { label: "Raw spreads", meta: "from 0.0 pips" },
  { label: "Swap-free accounts", meta: "unconditional" },
  { label: "Fast withdrawals", meta: "same-day" },
];

const SCORES = [92, 86, 79];

const Metric = ({ label, value, tone = "" }) => (
  <div data-testid={`engine-metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <p className={`t-stat-sm ${tone}`}>{value}</p>
    <p className="t-stat-label">{label}</p>
  </div>
);

export const EngineInterface = () => {
  const { brokers } = useBrokers();
  const RANKED = brokers.slice(0, 3);
  const N = brokers.length;
  const FEED = [
    { t: "22:04", msg: `Swap snapshot ingested → ${N} brokers, 9 instruments.` },
    { t: "22:04", msg: `Cost model re-ranked. ${RANKED[0]?.short || "—"} leads on all-in spread.` },
    { t: "22:03", msg: "Regulator check passed → FCA, ASIC, CySEC, FSCA." },
  ];
  const COSTS = brokers.slice(0, 5).map((b) => ({
    short: b.short,
    cost: +(b.eurUsdSpread * 10 + b.commission).toFixed(2),
  }));
  const MAX_COST = Math.max(...COSTS.map((c) => c.cost), 1);

  const [done, setDone] = useState(0);

  useEffect(() => {
    const t = [1, 2, 3].map((n, i) => setTimeout(() => setDone(n), 900 + i * 480));
    return () => t.forEach(clearTimeout);
  }, []);

  const top = RANKED[0];
  const progress = done === 3 ? 100 : done * 31;

  return (
    <div
      className="relative mx-auto flex h-full w-full flex-col overflow-hidden rounded-[22px] border border-[var(--line)] bg-white"
      style={{ boxShadow: "0 50px 120px -50px rgba(10,15,29,0.45), 0 2px 6px -2px rgba(10,15,29,0.08)" }}
      data-testid="hero-visual"
    >
      {/* browser chrome */}
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-[var(--line)] bg-[#fbfcfe] px-5">
        <div className="flex items-center gap-1.5">
          {["#e2e8f0", "#e2e8f0", "#e2e8f0"].map((c, i) => (
            <span key={i} className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />
          ))}
        </div>
        <p className="font-mono text-[11px] text-[var(--ink-4)]">forexbrokersignals.com&nbsp;/&nbsp;<span className="text-[var(--ink-3)]">finder</span></p>
        <div className="flex items-center gap-2">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--pos)] opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--pos)]" />
          </span>
          <span className="font-mono text-[10px] tracking-[0.04em] text-[var(--ink-3)]">Live</span>
        </div>
      </div>

      {/* body */}
      <div className="grid flex-1 grid-cols-1 lg:grid-cols-[300px_1fr]">
        {/* criteria / scan feed */}
        <div className="flex flex-col border-b border-[var(--line)] bg-[#fbfcfe] p-6 lg:border-b-0 lg:border-r" data-testid="engine-criteria">
          <p className="eyebrow">Your criteria</p>
          <ul className="mt-5 space-y-2.5">
            {CRITERIA.map((r, i) => (
              <li key={r.label} className="flex items-center justify-between rounded-xl border border-[var(--line)] bg-white px-4 py-3" data-testid={`engine-criterion-${i}`}>
                <div>
                  <p className="t-h6">{r.label}</p>
                  <p className="font-mono text-[11px] text-[var(--ink-3)]">{r.meta}</p>
                </div>
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-all duration-500 ${done > i ? "scale-100 bg-[var(--cobalt)] text-white" : "scale-90 bg-[var(--line)] text-transparent"}`}>
                  <Check size={13} strokeWidth={3} />
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-6 h-1 w-full overflow-hidden rounded-full bg-[var(--line)]">
            <motion.div className="h-full bg-[var(--cobalt)]" animate={{ width: `${progress}%` }} transition={{ duration: 0.9, ease }} />
          </div>
          <p className="mt-2.5 font-mono text-[11px] text-[var(--ink-3)]" data-testid="engine-scan-status">
            Scanning {done === 3 ? `${N} of ${N}` : `${Math.round((done * N) / 3)} of ${N}`} verified brokers
          </p>
          <div className="mt-7 border-t border-[var(--line)] pt-5">
            <p className="eyebrow">Engine log</p>
            <ul className="mt-3.5 space-y-2.5">
              {FEED.map((f, i) => (
                <li key={`${f.t}-${i}`} className="flex gap-3 font-mono text-[11px] leading-relaxed">
                  <span className="shrink-0 text-[var(--ink-4)]">{f.t}</span>
                  <span className={i === 0 ? "text-[var(--ink-2)]" : "text-[var(--ink-3)]"}>{f.msg}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-auto flex items-center gap-2 pt-7 text-[var(--ink-3)]">
            <ShieldCheck size={14} className="text-[var(--pos)]" />
            <span className="font-mono text-[10px] tracking-[0.04em]">Never pay-to-rank</span>
          </div>
        </div>

        {/* ranked matches */}
        <div className="flex flex-col p-6 lg:p-7" data-testid="engine-results">
          <div className="flex items-center justify-between">
            <p className="eyebrow">Ranked matches</p>
            <span className="font-mono text-[10px] tracking-[0.04em] text-[var(--ink-4)]">Updated 22:00 UTC</span>
          </div>

          {/* top match */}
          <div className="mt-5 flex items-center gap-5 rounded-2xl border border-[var(--cobalt-line)] bg-[var(--cobalt-soft)] p-5" data-testid="engine-top-match">
            <MatchRing value={SCORES[0]} size={64} stroke={4} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2.5">
                <BrokerMark broker={top} size={26} />
                <p className="t-h4 truncate">{top.short}</p>
              </div>
              <p className="mt-1.5 truncate font-mono text-[11px] text-[var(--ink-3)]">{top.spreadModel} · {top.entities[0].regulator} · {top.entities[1].regulator}</p>
            </div>
            <div className="hidden shrink-0 items-center gap-2 rounded-full bg-[var(--ink)] px-3.5 py-1.5 text-white sm:flex">
              <Zap size={12} className="text-[#8fb4ff]" />
              <span className="font-mono text-[10.5px]">{top.executionMs}ms</span>
            </div>
          </div>

          {/* runners up */}
          <div className="mt-3 space-y-2.5">
            {RANKED.slice(1).map((b, i) => (
              <div key={b.slug} className="flex items-center gap-4 rounded-xl border border-[var(--line)] px-5 py-3.5" data-testid={`engine-match-${i + 1}`}>
                <BrokerMark broker={b} size={24} />
                <p className="t-h6 min-w-0 flex-1 truncate">{b.short}</p>
                <div className="hidden h-1 w-28 overflow-hidden rounded-full bg-[var(--line)] sm:block">
                  <motion.div className="h-full bg-[var(--ink-4)]" animate={{ width: done === 3 ? `${SCORES[i + 1]}%` : "0%" }} transition={{ duration: 1, ease, delay: 0.1 * i }} />
                </div>
                <p className="w-9 shrink-0 text-right font-mono text-[12px] text-[var(--ink-3)]">{SCORES[i + 1]}</p>
              </div>
            ))}
          </div>

          {/* metric strip */}
          <div className="mt-6 grid grid-cols-2 gap-6 border-t border-[var(--line)] pt-5 sm:grid-cols-4">
            <Metric label="EUR/USD spread" value={`${top.eurUsdSpread.toFixed(1)}p`} />
            <Metric label="Commission / lot" value={`$${top.commission.toFixed(2)}`} />
            <Metric label="Swap long" value={`−$${Math.abs(top.swaps.EURUSD.long).toFixed(2)}`} tone="!text-[var(--neg)]" />
            <Metric label="Withdrawal" value="4h" tone="!text-[var(--pos)]" />
          </div>

          {/* all-in cost comparison */}
          <div className="mt-7 flex flex-1 flex-col border-t border-[var(--line)] pt-5" data-testid="engine-cost-chart">
            <div className="flex items-baseline justify-between">
              <p className="eyebrow">All-in cost per lot · EUR/USD</p>
              <span className="font-mono text-[10px] tracking-[0.04em] text-[var(--ink-4)]">USD, round turn</span>
            </div>
            <div className="mt-4 space-y-3">
              {COSTS.map((c, i) => (
                <div key={c.short} className="flex items-center gap-4">
                  <span className="w-28 shrink-0 truncate font-mono text-[11px] text-[var(--ink-3)]">{c.short}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--line)]">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: i === 0 ? "var(--cobalt)" : "var(--ink-4)" }}
                      animate={{ width: done === 3 ? `${(c.cost / MAX_COST) * 100}%` : "0%" }}
                      transition={{ duration: 1, ease, delay: 0.08 * i }}
                    />
                  </div>
                  <span className={`w-14 shrink-0 text-right font-mono text-[12px] ${i === 0 ? "text-[var(--cobalt)]" : "text-[var(--ink-3)]"}`}>${c.cost.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
